import React, { useEffect, useState } from 'react';
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  message, 
  Popconfirm, 
  Select, 
  DatePicker, 
  InputNumber,
  Card,
  Row,
  Col,
  Tag,
  Space,
  Tooltip
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  DownloadOutlined,
  CalculatorOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  MailOutlined
} from '@ant-design/icons';
import {
  getPayrolls,
  generatePayroll,
  bulkGeneratePayroll,
  updatePayroll,
  deletePayroll,
  downloadSalarySlip,
  getEmployees,
  getDepartments,
  exportPayrollsPdf,
  sendPayslip,
  getEmployeeLoansByEmployee,
  getActiveEmployeeLoan,
  previewSalarySlip
} from '../api';
import moment from 'moment';

const { Option } = Select;
const { TextArea } = Input;

const formatNum = (val) => {
  if (val === null || val === undefined || isNaN(val) || val === '') return '0.00';
  return Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const Payroll = () => {
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [bulkModalVisible, setBulkModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({
    month_year: moment().format('YYYY-MM'),
    employee_id: '',
    status: '',
    department_id: '',
    search: ''
  });
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [bulkForm] = Form.useForm();

  // Real-time calculation for edit form
  const [editCalculations, setEditCalculations] = useState({
    totalEarnings: 0,
    totalDeductions: 0,
    netAmount: 0
  });

  const [sendingPayslipId, setSendingPayslipId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [sendAllCount, setSendAllCount] = useState({ sent: 0, total: 0 });
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const [loanInfo, setLoanInfo] = useState(null);

  // 1. Add state for the loan history modal
  const [loanHistoryModalVisible, setLoanHistoryModalVisible] = useState(false);
  const [loanHistory, setLoanHistory] = useState([]);

  const updateEditCalculations = (values) => {
    const baseEarnings = parseFloat(editing?.basic_salary || 0) + parseFloat(editing?.hourly_wage || 0) + parseFloat(editing?.overtime_pay || 0);
    const siteAllowance = parseFloat(values.site_allowance || 0);
    const otherAllowance = parseFloat(values.other_allowance || 0);
    const housekeeping = parseFloat(values.housekeeping || 0);
    const otherDeductions = parseFloat(values.other_deductions || 0);
    const loanDeduction = parseFloat(values.loan_deduction || 0);

    const totalEarnings = baseEarnings + siteAllowance + otherAllowance;
    const totalDeductions = housekeeping + otherDeductions + loanDeduction;
    const netAmount = totalEarnings - totalDeductions;

    setEditCalculations({
      totalEarnings: isNaN(totalEarnings) ? 0 : totalEarnings,
      totalDeductions: isNaN(totalDeductions) ? 0 : totalDeductions,
      netAmount: isNaN(netAmount) ? 0 : netAmount
    });
  };

  // Update calculations when form values change
  useEffect(() => {
    if (editModalVisible && editing) {
      const values = editForm.getFieldsValue();
      updateEditCalculations(values);
    }
  }, [editModalVisible, editing]);

  const handleEditFormChange = (changedValues, allValues) => {
    updateEditCalculations(allValues);
  };

  const fetchPayrolls = async () => {
    setLoading(true);
    const res = await getPayrolls(filters);
    setLoading(false);
    if (res.success) {
      setPayrolls(res.data);
    } else {
      message.error(res.message || 'Failed to fetch payrolls');
    }
  };

  const fetchEmployees = async () => {
    const res = await getEmployees();
    if (res.success) {
      setEmployees(res.data);
    }
  };

  const fetchDepartments = async () => {
    const res = await getDepartments();
    if (res.success) {
      setDepartments(res.data);
    }
  };

  useEffect(() => {
    fetchPayrolls();
    fetchEmployees();
    fetchDepartments();
  }, [filters]);

  const openModal = () => {
    setModalVisible(true);
    form.resetFields();
    form.setFieldsValue({
      month_year: moment()
    });
  };

  const openEditModal = async (record) => {
    setEditing(record);
    setEditModalVisible(true);
    editForm.setFieldsValue({
      site_allowance: record.site_allowance,
      other_allowance: record.other_allowance,
      housekeeping: record.housekeeping,
      other_deductions: record.other_deductions,
      loan_deduction: record.loan_deduction,
      status: record.status || 'draft',
      remarks: record.remarks
    });

    // Fetch only the active loan for the employee
    const empId = record.employee_id || record.employee?.id;
    if (empId) {
      const res = await getActiveEmployeeLoan(empId);
      console.log("gggggggggg"+res)
      if (res.success && res.data) {
        setLoanInfo(res.data.data);
      } else {
        setLoanInfo(null);
      }
    } else {
      setLoanInfo(null);
    }
  };

  const openBulkModal = () => {
    setBulkModalVisible(true);
    bulkForm.resetFields();
    bulkForm.setFieldsValue({
      month_year: moment()
    });
  };

  const handleGeneratePayroll = async () => {
    try {
      const values = await form.validateFields();
      // Convert moment object to YYYY-MM format
      if (values.month_year) {
        values.month_year = values.month_year.format('YYYY-MM');
      }
      const res = await generatePayroll(values);
      if (res.success) {
        message.success('Payroll generated successfully');
        setModalVisible(false);
        fetchPayrolls();
      } else {
        message.error(res.message || 'Failed to generate payroll');
      }
    } catch (err) {
      // Validation error
    }
  };

  const handleBulkGenerate = async () => {
    try {
      const values = await bulkForm.validateFields();
      // Convert moment object to YYYY-MM format
      if (values.month_year) {
        values.month_year = values.month_year.format('YYYY-MM');
      }
      const res = await bulkGeneratePayroll(values);
      if (res.success) {
        message.success(`Generated payroll for ${res.data.generated} employees`);
        if (res.data.errors.length > 0) {
          message.warning(`${res.data.errors.length} errors occurred`);
        }
        setBulkModalVisible(false);
        fetchPayrolls();
      } else {
        message.error(res.message || 'Failed to generate payrolls');
      }
    } catch (err) {
      // Validation error
    }
  };

  const handleUpdatePayroll = async () => {
    try {
      const values = await editForm.validateFields();
      console.log(values)
      const res = await updatePayroll(editing.id, values);
      if (res.success) {
        message.success('Payroll updated successfully');
        setEditModalVisible(false);
        fetchPayrolls();
      } else {
        message.error(res.message || 'Failed to update payroll');
      }
    } catch (err) {
      // Validation error
    }
  };

  const handleDelete = async (id) => {
    const res = await deletePayroll(id);
    if (res.success) {
      message.success('Payroll deleted');
      fetchPayrolls();
    } else {
      message.error(res.message || 'Delete failed');
    }
  };

  const handleDownloadSlip = async (id) => {
    try {
      message.loading('Downloading salary slip...', 0);
      await downloadSalarySlip(id);
      message.destroy();
      message.success('Salary slip downloaded successfully');
    } catch (err) {
      message.destroy();
      message.error('Failed to download salary slip: ' + (err.message || 'Unknown error'));
    }
  };

  const handlePreviewSlip = async (id) => {
    try {
      message.loading('Opening preview...', 0);
      await previewSalarySlip(id);
      message.destroy();
    } catch (err) {
      message.destroy();
      message.error('Failed to preview salary slip: ' + (err.message || 'Unknown error'));
    }
  };

  const sendPayslip = async (id) => {
    setSendingPayslipId(id);
    const res = await sendPayslip(id);
    setSendingPayslipId(null);
    if (res.success) {
      message.success('Payslip sent successfully');
    } else {
      message.error(res.message || 'Failed to send payslip');
    }
  };

  const sendAllPayslips = async () => {
    setSendingAll(true);
    const ids = payrolls.map(p => p.id);
    setSendAllCount({ sent: 0, total: ids.length });
    for (let i = 0; i < ids.length; i++) {
      await sendPayslip(ids[i]);
      setSendAllCount({ sent: i + 1, total: ids.length });
    }
    setSendingAll(false);
    message.success('All payslips sent!');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'orange';
      case 'approved': return 'blue';
      case 'paid': return 'green';
      default: return 'default';
    }
  };

  const columns = [
    { 
      title: 'Employee', 
      dataIndex: ['employee', 'name'], 
      key: 'employee_name',
      render: (name, record) => (
        <div>
          <div>{name}</div>
          <small style={{ color: '#666' }}>{record.employee?.emp_code}</small>
        </div>
      )
    },
    { 
      title: 'Month', 
      dataIndex: 'month_year', 
      key: 'month_year',
      render: (month) => moment(month + '-01').format('MMMM YYYY')
    },
    { 
      title: 'Hours Worked', 
      dataIndex: 'total_hours_worked', 
      key: 'total_hours_worked',
      render: (hours) => hours || 0
    },
    { 
      title: 'Overtime', 
      dataIndex: 'overtime_hours', 
      key: 'overtime_hours',
      render: (hours) => hours || 0
    },
    { 
      title: 'Total Earnings', 
      dataIndex: 'total_earnings', 
      key: 'total_earnings',
      render: (amount) => formatNum(amount)
    },
    { 
      title: 'Loan Deduction', 
      dataIndex: 'loan_deduction', 
      key: 'loan_deduction',
      render: (amount) => amount > 0 ? formatNum(amount) : '-'
    },
    { 
      title: 'Housekeeping', 
      dataIndex: 'housekeeping', 
      key: 'housekeeping',
      render: (amount) => amount > 0 ? formatNum(amount) : '-'
    },
    { 
      title: 'Other Deductions', 
      dataIndex: 'other_deductions', 
      key: 'other_deductions',
      render: (amount) => amount > 0 ? formatNum(amount) : '-'
    },
    { 
      title: 'Total Deductions', 
      dataIndex: 'total_deductions', 
      key: 'total_deductions',
      render: (amount) => formatNum(amount)
    },
    { 
      title: 'Net Amount', 
      dataIndex: 'net_amount', 
      key: 'net_amount',
      render: (amount) => (
        <strong style={{ color: '#28a745' }}>
          {formatNum(amount)}
        </strong>
      )
    },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Payroll">
            <Button 
              icon={<EditOutlined />} 
              size="small" 
              onClick={() => openEditModal(record)} 
            />
          </Tooltip>
          <Tooltip title="Preview Salary Slip">
            <Button 
              icon={<FileTextOutlined />} 
              size="small" 
              onClick={() => handlePreviewSlip(record.id)}
            />
          </Tooltip>
          <Tooltip title="Download Salary Slip">
            <Button 
              icon={<DownloadOutlined />} 
              size="small" 
              onClick={() => handleDownloadSlip(record.id)}
            />
          </Tooltip>
          <Tooltip title="Send Payslip via Email">
            <Button
              icon={<MailOutlined />}
              size="small"
              loading={sendingPayslipId === record.id}
              onClick={() => sendPayslip(record.id)}
            />
          </Tooltip>
          <Popconfirm 
            title="Are you sure to delete this payroll?" 
            onConfirm={() => handleDelete(record.id)} 
            okText="Yes" 
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 2. Define openLoanHistoryModal
  const openLoanHistoryModal = (loan) => {
    setLoanHistory(loan.transactions || []);
    setLoanHistoryModalVisible(true);
  };
console.log(loanInfo)
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <h2>Payroll Management</h2>
          </Col>
          <Col>
            <Space>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={openModal}
              >
                Generate Payroll
              </Button>
              <Button 
                icon={<CalculatorOutlined />} 
                onClick={openBulkModal}
              >
                Bulk Generate
              </Button>
              <Button
                icon={<MailOutlined />}
                onClick={sendAllPayslips}
                loading={sendingAll}
              >
                Send All Payslips
                {sendingAll && (
                  <span style={{ marginLeft: 8 }}>
                    ({sendAllCount.sent}/{sendAllCount.total})
                  </span>
                )}
              </Button>
              <Button
                icon={<FilePdfOutlined />}
                loading={downloadingPdf}
                onClick={async () => {
                  setDownloadingPdf(true);
                  try {
                    const blob = await exportPayrollsPdf({
                      month_year: filters.month_year
                    });
                    
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `payrolls-${filters.month_year || 'report'}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error('Error downloading PDF:', error);
                    message.error(error.message || 'Failed to download PDF');
                  } finally {
                    setDownloadingPdf(false);
                  }
                }}
              >
                Download PDF
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Select
              placeholder="Select Month"
              value={filters.month_year}
              onChange={(value) => setFilters({...filters, month_year: value})}
              style={{ width: '100%' }}
            >
              {Array.from({ length: 12 }, (_, i) => {
                const date = moment().subtract(i, 'months');
                return (
                  <Option key={date.format('YYYY-MM')} value={date.format('YYYY-MM')}>
                    {date.format('MMMM YYYY')}
                  </Option>
                );
              })}
            </Select>
          </Col>
   
          <Col span={6}>
            <Select
              placeholder="Select Department"
              value={filters.department_id}
              onChange={value => setFilters({...filters, department_id: value})}
              style={{ width: '100%' }}
              allowClear
            >
              {departments.map(dep => (
                <Option key={dep.id} value={dep.id}>{dep.name}</Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Input
              placeholder="Search by name or code"
              value={filters.search}
              onChange={e => setFilters({...filters, search: e.target.value})}
              allowClear
            />
          </Col>
        </Row>
      </Card>

      <Table 
        rowKey="id" 
        columns={columns} 
        dataSource={payrolls} 
        loading={loading} 
        bordered 
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
        }}
      />

      {/* Generate Payroll Modal */}
      <Modal
        title="Generate Payroll"
        open={modalVisible}
        onOk={handleGeneratePayroll}
        onCancel={() => setModalVisible(false)}
        okText="Generate"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item 
            name="employee_id" 
            label="Employee" 
            rules={[{ required: true, message: 'Please select employee' }]}
          >
            <Select placeholder="Select Employee">
              {employees.map(emp => (
                <Option key={emp.id} value={emp.id}>{emp.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item 
            name="month_year" 
            label="Month" 
            rules={[{ required: true, message: 'Please select month' }]}
          >
            <DatePicker 
              picker="month" 
              style={{ width: '100%' }} 
              format="YYYY-MM"
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="site_allowance" label="Site Allowance">
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={0} 
                  placeholder="0.00"
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="other_allowance" label="Other Allowance">
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={0} 
                  placeholder="0.00"
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="housekeeping" label="Housekeeping">
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={0} 
                  placeholder="0.00"
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="other_deductions" label="Other Deductions">
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={0} 
                  placeholder="0.00"
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="loan_deduction" label="Loan Deduction">
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={0} 
                  placeholder="0.00"
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remarks" label="Remarks">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Payroll Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 40 }}>
            <span>Edit Payroll - {editing?.employee?.name}</span>
            <Button type="primary" onClick={handleUpdatePayroll}>Save Changes</Button>
          </div>
        }
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width="100%"
        style={{ 
          top: 0, 
          margin: 0, 
          padding: 0,
          maxWidth: '100vw',
          height: '100vh'
        }}
        bodyStyle={{ 
          height: 'calc(100vh - 55px)', 
          overflow: 'auto',
          margin: 0,
          padding: '12px'
        }}
        maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
      >
        {editing && (
          <div>
            {/* Employee Information - Compact */}
            <Card size="small" title="Employee Information" style={{ marginBottom: 8 }}>
              <Row gutter={8}>
                <Col span={6}>
                  <strong>Name:</strong> {editing.employee?.name}
                </Col>
                <Col span={6}>
                  <strong>Code:</strong> {editing.employee?.emp_code}
                </Col>
                <Col span={6}>
                  <strong>Department:</strong> {editing.employee?.department?.name}
                </Col>
                <Col span={6}>
                  <strong>Month:</strong> {moment(editing.month_year + '-01').format('MMMM YYYY')}
                </Col>
              </Row>
              <Row gutter={8} style={{ marginTop: 4 }}>
                <Col span={6}>
                  <strong>Hours:</strong> {editing.total_hours_worked || 0}
                </Col>
                <Col span={6}>
                  <strong>Overtime:</strong> {editing.overtime_hours || 0}
                </Col>
                <Col span={6}>
                  <strong>Hourly Rate:</strong> {formatNum(editing.employee?.hourly_rate || 0)}
                </Col>
                <Col span={6}>
                  <strong>Status:</strong>
                  <Select
                    size="small"
                    style={{ marginLeft: 8, width: 100 }}
                    value={editForm.getFieldValue('status')}
                    onChange={value => editForm.setFieldsValue({ status: value })}
                  >
                    <Option value="draft">Draft</Option>
                    <Option value="approved">Approved</Option>
                    <Option value="paid">Paid</Option>
                  </Select>
                </Col>
              </Row>
            </Card>

            {/* Base Calculations - Compact */}
            <Card size="small" title="Base Calculations" style={{ marginBottom: 8 }}>
              <Row gutter={8}>
                <Col span={6}>
                  <div style={{ textAlign: 'center', padding: 8, background: '#f6f8fa', borderRadius: 4 }}>
                    <div style={{ fontSize: 10, color: '#666' }}>Basic Salary</div>
                    <div style={{ fontSize: 14, fontWeight: 'bold' }}>{formatNum(editing.basic_salary || 0)}</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: 'center', padding: 8, background: '#f6f8fa', borderRadius: 4 }}>
                    <div style={{ fontSize: 10, color: '#666' }}>Hourly Wage</div>
                    <div style={{ fontSize: 14, fontWeight: 'bold' }}>{formatNum(editing.hourly_wage || 0)}</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: 'center', padding: 8, background: '#f6f8fa', borderRadius: 4 }}>
                    <div style={{ fontSize: 10, color: '#666' }}>Overtime Pay</div>
                    <div style={{ fontSize: 14, fontWeight: 'bold' }}>{formatNum(editing.overtime_pay || 0)}</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: 'center', padding: 8, background: '#e6f7ff', borderRadius: 4 }}>
                    <div style={{ fontSize: 10, color: '#666' }}>Base Earnings</div>
                    <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1890ff' }}>
                      {formatNum((editing.basic_salary || 0) + (editing.hourly_wage || 0) + (editing.overtime_pay || 0))}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* Editable Allowances and Deductions - Compact */}
            <Form 
              form={editForm} 
              layout="vertical" 
              onValuesChange={handleEditFormChange}
              size="small"
              style={{ marginBottom: 8 }}
            >
              <Form.Item name="status" style={{ display: 'none' }}>
                <Input />
              </Form.Item>
              <Row gutter={8}>
                <Col span={12}>
                  <Card size="small" title="Allowances" style={{ marginBottom: 8 }}>
                    <Row gutter={8}>
                      <Col span={12}>
                        <Form.Item name="site_allowance" label="Site Allowance" style={{ marginBottom: 8 }}>
                          <InputNumber 
                            style={{ width: '100%' }} 
                            min={0} 
                            placeholder="0.00"
                            size="small"
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/(,*)/g, '')}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="other_allowance" label="Other Allowance" style={{ marginBottom: 8 }}>
                          <InputNumber 
                            style={{ width: '100%' }} 
                            min={0} 
                            placeholder="0.00"
                            size="small"
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/(,*)/g, '')}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card size="small" title="Deductions" style={{ marginBottom: 8 }}>
                    <Row gutter={8}>
                      <Col span={8}>
                        <Form.Item name="housekeeping" label="Housekeeping" style={{ marginBottom: 8 }}>
                          <InputNumber 
                            style={{ width: '100%' }} 
                            min={0} 
                            placeholder="0.00"
                            size="small"
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/(,*)/g, '')}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="other_deductions" label="Other Deductions" style={{ marginBottom: 8 }}>
                          <InputNumber 
                            style={{ width: '100%' }} 
                            min={0} 
                            placeholder="0.00"
                            size="small"
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/(,*)/g, '')}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="loan_deduction" label="Loan Deduction" style={{ marginBottom: 8 }}>
                          <InputNumber 
                            style={{ width: '100%' }} 
                            min={0} 
                            placeholder="0.00"
                            size="small"
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/(,*)/g, '')}
                          />
                        </Form.Item>
                      </Col>
                      {loanInfo ? (
                        <div style={{ fontSize: 12, color: '#888', marginTop: -8 }}>
                          Loan Status: {loanInfo.status}<br/>
                          Loan Amount: {formatNum(loanInfo.loan_amount)}<br/>
                          Remaining Balance: {formatNum(loanInfo.remaining_balance)}<br/>
                          Monthly Deduction: {formatNum(loanInfo.monthly_deduction)}<br/>
                          <a href="#" onClick={() => openLoanHistoryModal(loanInfo)}>View Loan History</a><br/>
                          (Loan deduction will be deducted from loan account)
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: '#888', marginTop: -8 }}>
                          Loan history not found
                        </div>
                      )}
                    </Row>
                  </Card>
                </Col>
              </Row>

              {/* Remarks */}
              <Form.Item name="remarks" label="Remarks" style={{ marginBottom: 8 }}>
                <TextArea rows={2} size="small" />
              </Form.Item>
            </Form>

            {/* Real-time Calculations - Compact */}
            <Card size="small" title="Payroll Summary" style={{ marginBottom: 8 }}>
              <Row gutter={8}>
                <Col span={8}>
                  <div style={{ textAlign: 'center', padding: 12, background: '#f6ffed', borderRadius: 4 }}>
                    <div style={{ fontSize: 11, color: '#666' }}>Total Earnings</div>
                    <div style={{ fontSize: 16, fontWeight: 'bold', color: '#52c41a' }}>
                      {formatNum(editCalculations.totalEarnings)}
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center', padding: 12, background: '#fff2e8', borderRadius: 4 }}>
                    <div style={{ fontSize: 11, color: '#666' }}>Total Deductions</div>
                    <div style={{ fontSize: 16, fontWeight: 'bold', color: '#fa8c16' }}>
                      {formatNum(editCalculations.totalDeductions)}
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center', padding: 12, background: '#e6f7ff', borderRadius: 4 }}>
                    <div style={{ fontSize: 11, color: '#666' }}>Net Amount</div>
                    <div style={{ fontSize: 16, fontWeight: 'bold', color: '#1890ff' }}>
                      {formatNum(editCalculations.netAmount)}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </div>
        )}
      </Modal>

      {/* Bulk Generate Modal */}
      <Modal
        title="Bulk Generate Payroll"
        open={bulkModalVisible}
        onOk={handleBulkGenerate}
        onCancel={() => setBulkModalVisible(false)}
        okText="Generate All"
        width={400}
      >
        <Form form={bulkForm} layout="vertical">
          <Form.Item 
            name="month_year" 
            label="Month" 
            rules={[{ required: true, message: 'Please select month' }]}
          >
            <DatePicker 
              picker="month" 
              style={{ width: '100%' }} 
              format="YYYY-MM"
            />
          </Form.Item>
          <div style={{ padding: 16, background: '#f6f8fa', borderRadius: 6 }}>
            <p style={{ margin: 0, color: '#666' }}>
              This will generate payroll for all active employees for the selected month.
              Existing payrolls will be skipped.
            </p>
          </div>
        </Form>
      </Modal>

      {/* Loan Transaction History Modal */}
      <Modal
        title="Loan Transaction History"
        open={loanHistoryModalVisible}
        onCancel={() => setLoanHistoryModalVisible(false)}
        footer={null}
        width={700}
      >
        <Table
          dataSource={loanHistory}
          rowKey="id"
          size="small"
          pagination={false}
          columns={[
            { title: 'Date', dataIndex: 'created_at', key: 'created_at', render: date => date ? moment(date).format('DD/MM/YYYY') : '' },
            { title: 'Type', dataIndex: 'type', key: 'type', render: type => <Tag color={type === 'payment' ? 'green' : 'blue'}>{type?.toUpperCase()}</Tag> },
            { title: 'Amount', dataIndex: 'amount', key: 'amount', render: amount => `AED ${parseFloat(amount).toFixed(2)}` },
            { title: 'Balance After', dataIndex: 'balance_after', key: 'balance_after', render: balance => `AED ${parseFloat(balance).toFixed(2)}` },
            { title: 'Reference', dataIndex: 'reference', key: 'reference' },
            { title: 'Remarks', dataIndex: 'remarks', key: 'remarks' },
          ]}
        />
      </Modal>
    </div>
  );
};

export default Payroll; 
